import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import loreCornerDetail from '../../assets/lore/lore_details.png';
import buttonWell from '../../assets/button_well.png';

function CornerDetail({ className = '', rotationClass = '' }) {
  return (
    <img
      src={loreCornerDetail}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none absolute h-[52px] w-[52px] select-none opacity-80 ${rotationClass} ${className}`}
    />
  );
}

export function WellModal({
  open,
  trinkets,
  onConfirm,
  onClose,
  error,
  resultText,
}) {
  const [selectedRelicId, setSelectedRelicId] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setSelectedRelicId(null);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const list = Array.isArray(trinkets) ? trinkets : [];
  const hasTrinkets = list.length > 0;
  const canConfirm = hasTrinkets && Boolean(selectedRelicId);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={() => onClose?.()}
        >
          <motion.div
            className="relative z-[1] w-[780px] max-w-[94vw] bg-black/80 px-6 pb-8 pt-5 text-white shadow-[0_20px_60px_rgba(0,0,0,0.72)] sm:px-8 sm:pb-10 sm:pt-6"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ fontFamily: 'Karma, serif' }}
          >
            <CornerDetail className="left-4 top-4" rotationClass="rotate-180" />
            <CornerDetail className="right-4 top-4" rotationClass="-rotate-90" />
            <CornerDetail className="left-4 bottom-4" rotationClass="rotate-90" />
            <CornerDetail className="right-4 bottom-4" />

            <div className="relative z-[1] mx-auto flex max-h-[82svh] w-full max-w-[720px] flex-col items-center pt-6 sm:pt-8">
              <h2 className="text-center text-[18px] font-semibold text-white sm:text-[20px]">
                The Well
              </h2>

              {resultText ? (
                <>
                  <div className="mt-5 min-h-0 w-full max-w-[520px] flex-1 overflow-y-auto pr-1 text-center text-[18px] leading-[1.35] whitespace-pre-wrap text-white/90">
                    {resultText}
                  </div>

                  <div className="mt-6 flex w-full justify-center">
                    <div
                      className="relative w-full max-w-[430px]"
                      style={{ aspectRatio: '553 / 159' }}
                    >
                      <img
                        src={buttonWell}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => onClose?.()}
                        className="absolute inset-0 appearance-none border-0 bg-transparent p-0 text-white outline-none transition hover:opacity-90 active:scale-[0.99]"
                        style={{ fontFamily: 'Karma, serif' }}
                      >
                        <span className="flex h-full w-full items-center justify-center text-[18px] translate-y-[2px]">
                          Continue
                        </span>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-4 w-full max-w-[560px] text-center text-[18px] leading-[1.25] text-white/90">
                    A narrow stone well descends into darkness.
                    <br />
                    Whatever you drop may never return.
                  </div>

                  <div className="mt-5 min-h-0 w-full flex-1 overflow-y-auto pr-1">
                    {!hasTrinkets ? (
                      <div className="rounded-xl border border-white/10 bg-black/30 p-3 text-center text-sm text-white/70">
                        Your satchel is empty.
                      </div>
                    ) : (
                      <>
                        <div className="mb-3 text-center text-sm uppercase tracking-widest text-white/60">
                          Choose a trinket to discard
                        </div>

                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                          {list.map((r) => {
                            const id = r?.id;
                            if (!id) return null;
                            const selected = selectedRelicId === id;

                            return (
                              <button
                                key={id}
                                type="button"
                                onClick={() => setSelectedRelicId(id)}
                                className={[
                                  'rounded-[14px] border bg-black/90 p-2 text-left transition',
                                  selected
                                    ? 'border-white/35 bg-white/[0.04]'
                                    : 'border-white/10 hover:bg-white/[0.04]',
                                ].join(' ')}
                              >
                                {r?.image ? (
                                  <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-[10px] bg-black/40">
                                    <img
                                      src={r.image}
                                      alt={r.name || r.title || ''}
                                      className="h-full w-full object-contain"
                                      draggable={false}
                                    />
                                  </div>
                                ) : (
                                  <div className="flex aspect-square w-full items-center justify-center rounded-[10px] bg-white/5 text-white/70">
                                    No image
                                  </div>
                                )}

                                <div className="mt-2 text-sm font-semibold leading-tight text-white truncate">
                                  {r?.name || r?.title || r?.id}
                                </div>

                                {r?.description && (
                                  <div
                                    className="mt-1 text-xs leading-snug text-white/60 overflow-hidden"
                                    style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                    title={r.description}
                                  >
                                    {r.description}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        <div className="mt-3 text-center text-xs text-white/60">
                          The chosen trinket will be removed from your inventory.
                        </div>
                      </>
                    )}

                    {error && (
                      <div className="mt-3 rounded-xl border border-red-500/40 bg-red-900/20 p-3 text-sm text-red-200">
                        {error}
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex w-full justify-center">
                    <div
                      className="relative w-full max-w-[430px]"
                      style={{ aspectRatio: '553 / 159' }}
                    >
                      <img
                        src={buttonWell}
                        alt=""
                        aria-hidden="true"
                        draggable={false}
                        className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
                      />

                      <button
                        type="button"
                        disabled={!canConfirm}
                        onClick={() => onConfirm?.({ selectedRelicId })}
                        title={
                          !hasTrinkets
                            ? 'No trinkets to discard'
                            : !selectedRelicId
                              ? 'Choose a trinket'
                              : undefined
                        }
                        className="absolute left-0 top-0 h-full w-1/2 appearance-none border-0 bg-transparent p-0 text-white outline-none transition hover:opacity-90 active:scale-[0.99] disabled:cursor-default disabled:opacity-45"
                        style={{ fontFamily: 'Karma, serif' }}
                      >
                        <span className="flex h-full w-full items-center justify-center pr-3 text-[18px] translate-y-[2px]">
                          Forsake
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => onClose?.()}
                        className="absolute right-0 top-0 h-full w-1/2 appearance-none border-0 bg-transparent p-0 text-white outline-none transition hover:opacity-90 active:scale-[0.99]"
                        style={{ fontFamily: 'Karma, serif' }}
                      >
                        <span className="flex h-full w-full items-center justify-center pl-3 text-[18px] translate-y-[2px]">
                          Leave
                        </span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
